import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  PalType,
  LivePal,
  GearPal,
  livePalInitialData,
  gearPalInitialData,
} from "../../types/Pal";
import AddOrEditPalFormHeader from "./AddOrEditPalFormHeader";
import GearPalForm from "./GearPalForm";
import LivePalForm from "./LivePalForm";
import { CreateLivePalFormSchema } from "../../forms/CreateLivePalFormSchema";
import { CreateGearPalFormSchema } from "../../forms/CreateGearPalFormSchema";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  addLivePal,
  CreateLivePalData,
  updateLivePal,
  UpdateLivePalData,
} from "../../backend/api/livepal";
import {
  createGearpal,
  CreateGearPalData,
  updateGearPal,
  UpdateGearPalData,
} from "../../backend/api/gearlpal";
import { toast } from "react-hot-toast";
import {
  getCoordinates,
  GetCoordinatesData,
  GetCoordinatesResponse,
} from "../../backend/api/geo";

type CreateLivePalForm = z.infer<typeof CreateLivePalFormSchema>;
type CreateGearPalForm = z.infer<typeof CreateGearPalFormSchema>;

interface AddOrEditPalFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess: (updatedPal?: LivePal | GearPal) => void;
  palType?: PalType | null; // Nuovo prop per specificare il tipo
  isEdit?: boolean;
  palToEdit?: LivePal | GearPal;
}

type GearPalItemForm = {
  name: string;
  categoryId: string;
  externalLink?: string;
  categoryLabel?: string;
};

const AddOrEditPalForm: React.FC<AddOrEditPalFormProps> = ({
  isOpen,
  onClose,
  onSubmitSuccess,
  palType: propPalType,
  isEdit = false,
  palToEdit = null,
}) => {
  const { t } = useTranslation();
  const [palType, setPalType] = useState<PalType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  console.log("AddOrEditPalForm -> isEdit", isEdit);

  // TanStack Query
  const queryClient = useQueryClient();

  // Mutation per creare LivePal
  const [generateCoordinatesLoading, setGenerateCoordinatesLoading] =
    useState(false);
  const { mutate: createLivePalMutation, isPending: isAddingLivePal } =
    useMutation({
      mutationFn: (data: CreateLivePalData) =>
        addLivePal(data, t("common.language", "it")),
      onSuccess: () => {
        // Invalida le query per aggiornare i dati
        queryClient.invalidateQueries({ queryKey: ["livepals"] });
        queryClient.invalidateQueries({ queryKey: ["my-livepals"] });

        // Reset del form
        resetLivePalForm();

        // Callback di successo
        onSubmitSuccess();
        onClose();
        setGenerateCoordinatesLoading(false);

        // Mostra messaggio di successo
        toast.success(
          t("forms.livePalCreated", "LivePal creato con successo!")
        );
      },
      onError: (error: any) => {
        console.error("Errore durante la creazione del LivePal:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          t("errors.unknownError", "Errore sconosciuto");
        toast.error(`${t("errors.error", "Errore")}: ${errorMessage}`);
        setGenerateCoordinatesLoading(false);
      },
    });

  // Mutation per creare GearPal
  const { mutate: createGearPalMutation, isPending: isAddingGearPal } =
    useMutation({
      mutationFn: (data: CreateGearPalData) =>
        createGearpal(data, t("common.language", "it")),
      onSuccess: () => {
        // Invalida le query per aggiornare i dati
        queryClient.invalidateQueries({ queryKey: ["gearpals"] });
        queryClient.invalidateQueries({ queryKey: ["my-gearpals"] });

        // Reset del form
        resetGearPalForm();

        // Callback di successo
        onSubmitSuccess();
        onClose();

        // Mostra messaggio di successo
        toast.success(
          t("forms.gearPalCreated", "GearPal creato con successo!")
        );
      },
      onError: (error: any) => {
        console.error("Errore durante la creazione del GearPal:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          t("errors.unknownError", "Errore sconosciuto");
        toast.error(`${t("errors.error", "Errore")}: ${errorMessage}`);
      },
    });

  // Mutation per aggiornare LivePal
  const { mutate: updateLivePalMutation, isPending: isUpdatingLivePal } =
    useMutation({
      mutationFn: ({ id, data }: { id: number; data: UpdateLivePalData }) =>
        updateLivePal(id, data, t("common.language", "it")),
      onSuccess: (response) => {
        // Invalida le query per aggiornare i dati
        queryClient.invalidateQueries({ queryKey: ["livepals"] });
        queryClient.invalidateQueries({ queryKey: ["my-livepals"] });

        // Reset del form
        resetLivePalForm();

        // Callback di successo con i dati aggiornati
        const updatedLivePal = response?.result as LivePal;
        onSubmitSuccess(updatedLivePal);
        onClose();
        setGenerateCoordinatesLoading(false);

        // Mostra messaggio di successo
        toast.success(
          t("forms.livePalUpdated", "LivePal aggiornato con successo!")
        );
      },
      onError: (error: any) => {
        console.error("Errore durante l'aggiornamento del LivePal:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          t("errors.unknownError", "Errore sconosciuto");
        toast.error(`${t("errors.error", "Errore")}: ${errorMessage}`);
        setGenerateCoordinatesLoading(false);
      },
    });

  // Mutation per aggiornare GearPal
  const { mutate: updateGearPalMutation, isPending: isUpdatingGearPal } =
    useMutation({
      mutationFn: ({ id, data }: { id: number; data: UpdateGearPalData }) =>
        updateGearPal(id, data, t("common.language", "it")),
      onSuccess: (response) => {
        // Invalida le query per aggiornare i dati
        queryClient.invalidateQueries({ queryKey: ["gearpals"] });
        queryClient.invalidateQueries({ queryKey: ["my-gearpals"] });

        // Reset del form
        resetGearPalForm();

        // Callback di successo con i dati aggiornati
        const updatedGearPal = response?.result as GearPal;
        onSubmitSuccess(updatedGearPal);
        onClose();

        // Mostra messaggio di successo
        toast.success(
          t("forms.gearPalUpdated", "GearPal aggiornato con successo!")
        );
      },
      onError: (error: any) => {
        console.error("Errore durante l'aggiornamento del GearPal:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          t("errors.unknownError", "Errore sconosciuto");
        toast.error(`${t("errors.error", "Errore")}: ${errorMessage}`);
      },
    });

  const { mutate: getCoordinatesMutation, isPending: isGettingCoordinates } =
    useMutation({
      mutationFn: (data: GetCoordinatesData) => getCoordinates(data, "it"),
      onSuccess: (data) => {
        console.log("Coordinate ottenute:", data);
      },
      onError: (error: any) => {
        console.error("Errore durante l'ottenimento delle coordinate:", error);
      },
    });

  const createLivePal = async (formData: CreateLivePalForm) => {
    console.log("handleLivePalFormSubmit chiamata!", formData);
    try {
      setGenerateCoordinatesLoading(true);

      // Ottieni le coordinate usando la mutation
      const coordinatesData: GetCoordinatesData = {
        address: formData.address,
        city: formData.city,
        zipCode: formData.zipCode,
        country: "Italy",
        palType: "LivePal",
      };

      const coordinates: GetCoordinatesResponse = await new Promise(
        (resolve, reject) => {
          getCoordinatesMutation(coordinatesData, {
            onSuccess: (data) => {
              resolve(data);
            },
            onError: (error) => {
              const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Errore sconosciuto";
              reject(new Error(errorMessage));
            },
          });
        }
      );

      // Imposta le coordinate nel form
      setLivePalValue("coordinates", coordinates.data);

      // Prepara i dati per l'API
      const apiData: CreateLivePalData = {
        name: formData.name,
        address: formData.address,
        zipCode: formData.zipCode,
        city: formData.city,
        region: formData.region,
        country: formData.country,
        website: formData.website || "",
        email: formData.email || "",
        capacity: formData.capacity,
        venueType: formData.venueType,
        accommodation: formData.accommodation,
        meals: formData.meals,
        coordinates: coordinates.data,
        contacts:
          formData.contacts?.map((contact) => ({
            type: contact.type,
            value: contact.value,
          })) || undefined,
        equipment: formData.equipment || undefined,
      };

      // Debug: log dei dati del form
      console.log("Form data:", formData);
      console.log("API data:", apiData);

      // Esegui la mutation
      createLivePalMutation(apiData);
    } catch (error) {
      setGenerateCoordinatesLoading(false);
      console.log("Error:", error);
      let message =
        error instanceof Error
          ? error.message
          : t("errors.unknownError", "Errore sconosciuto");
      toast.error(t("errors.error", "Errore") + ": " + message);
    }
  };

  const createGearPal = async (formData: CreateGearPalForm) => {
    console.log("handleGearPalFormSubmit chiamata!", formData);
    try {
      // Ottieni le coordinate usando la mutation
      const coordinatesData: GetCoordinatesData = {
        city: formData.city,
        zipCode: formData.zipcode,
        country: "Italy",
        palType: "GearPal",
      };

      const coordinates: GetCoordinatesResponse = await new Promise(
        (resolve, reject) => {
          getCoordinatesMutation(coordinatesData, {
            onSuccess: (data) => {
              resolve(data);
            },
            onError: (error) => {
              console.log("Error getting coordinates:", error);
              const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Errore sconosciuto";
              reject(new Error(errorMessage));
            },
          });
        }
      );

      // Aggiorna il form con le coordinate ottenute
      setGearPalValue("coordinates", coordinates.data);

      // Prepara i dati per l'API
      const apiData: CreateGearPalData = {
        ...formData,
        coordinates: coordinates.data,
      } as CreateGearPalData;

      // Esegui la mutation
      createGearPalMutation(apiData);
    } catch (error) {
      let message =
        error instanceof Error
          ? error.message
          : t("errors.unknownError", "Errore sconosciuto");
      toast.error(t("errors.error", "Errore") + ": " + message);
    }
  };

  const updateLivePalWithCoordinates = async (
    id: number,
    formData: CreateLivePalForm
  ) => {
    console.log("updateLivePalWithCoordinates chiamata!", formData);
    try {
      setGenerateCoordinatesLoading(true);

      const livePal = palToEdit as LivePal;
      let coordinates = livePal.coordinates;

      // Controlla se i campi geografici sono cambiati
      const locationChanged =
        formData.address !== livePal.address ||
        formData.city !== livePal.city ||
        formData.zipCode !== livePal.zipCode;

      // Se la posizione è cambiata, ricalcola le coordinate
      if (locationChanged) {
        const coordinatesData: GetCoordinatesData = {
          address: formData.address,
          city: formData.city,
          zipCode: formData.zipCode,
          country: "Italy",
          palType: "LivePal",
        };

        const coordinatesResponse: GetCoordinatesResponse = await new Promise(
          (resolve, reject) => {
            getCoordinatesMutation(coordinatesData, {
              onSuccess: (data) => {
                resolve(data);
              },
              onError: (error) => {
                const errorMessage =
                  error?.response?.data?.message ||
                  error?.message ||
                  "Errore sconosciuto";
                reject(new Error(errorMessage));
              },
            });
          }
        );

        coordinates = coordinatesResponse.data;
        setLivePalValue("coordinates", coordinates);
      }

      // Prepara i dati per l'API
      const apiData: UpdateLivePalData = {
        name: formData.name,
        address: formData.address,
        zipCode: formData.zipCode,
        city: formData.city,
        region: formData.region,
        country: formData.country,
        website: formData.website || "",
        email: formData.email || "",
        capacity: formData.capacity,
        venueType: formData.venueType,
        accommodation: formData.accommodation,
        meals: formData.meals,
        coordinates: coordinates,
        contacts:
          formData.contacts?.map((contact) => ({
            type: contact.type,
            value: contact.value,
          })) || undefined,
        equipment: formData.equipment || undefined,
      };

      console.log("Update API data:", apiData);

      // Esegui la mutation
      updateLivePalMutation({ id, data: apiData });
    } catch (error) {
      setGenerateCoordinatesLoading(false);
      console.log("Error:", error);
      let message =
        error instanceof Error
          ? error.message
          : t("errors.unknownError", "Errore sconosciuto");
      toast.error(t("errors.error", "Errore") + ": " + message);
    }
  };

  const updateGearPalWithCoordinates = async (
    id: number,
    formData: CreateGearPalForm
  ) => {
    console.log("updateGearPalWithCoordinates chiamata!", formData);
    try {
      const gearPal = palToEdit as GearPal;
      let coordinates = gearPal.coordinates;

      // Controlla se i campi geografici sono cambiati
      const locationChanged =
        formData.city !== gearPal.city || formData.zipcode !== gearPal.zipCode;

      // Se la posizione è cambiata, ricalcola le coordinate
      if (locationChanged) {
        const coordinatesData: GetCoordinatesData = {
          city: formData.city,
          zipCode: formData.zipcode,
          country: "Italy",
          palType: "GearPal",
        };

        const coordinatesResponse: GetCoordinatesResponse = await new Promise(
          (resolve, reject) => {
            getCoordinatesMutation(coordinatesData, {
              onSuccess: (data) => {
                resolve(data);
              },
              onError: (error) => {
                console.log("Error getting coordinates:", error);
                const errorMessage =
                  error?.response?.data?.message ||
                  error?.message ||
                  "Errore sconosciuto";
                reject(new Error(errorMessage));
              },
            });
          }
        );

        coordinates = coordinatesResponse.data;
        setGearPalValue("coordinates", coordinates);
      }

      // Prepara i dati per l'API
      const apiData: UpdateGearPalData = {
        name: formData.name,
        zipcode: formData.zipcode,
        city: formData.city,
        region: formData.region,
        country: formData.country,
        coordinates: coordinates,
        contacts:
          formData.contacts?.map((contact) => ({
            type: contact.type,
            value: contact.value,
          })) || undefined,
        items:
          formData.items?.map((item) => ({
            name: item.name,
            categoryId: item.categoryId,
            externalLink: item.externalLink,
          })) || undefined,
      };

      console.log("Update API data:", apiData);

      // Esegui la mutation
      updateGearPalMutation({ id, data: apiData });
    } catch (error) {
      let message =
        error instanceof Error
          ? error.message
          : t("errors.unknownError", "Errore sconosciuto");
      toast.error(t("errors.error", "Errore") + ": " + message);
    }
  };

  // Funzione per gestire il submit del form LivePal
  const handleLivePalFormSubmit = async (formData: CreateLivePalForm) => {
    if (isEdit && palToEdit && palToEdit.id) {
      await updateLivePalWithCoordinates(palToEdit.id, formData);
    } else {
      createLivePal(formData);
    }
  };

  // Funzione per gestire il submit del form GearPal
  const handleGearPalFormSubmit = async (formData: CreateGearPalForm) => {
    if (isEdit && palToEdit && palToEdit.id) {
      await updateGearPalWithCoordinates(palToEdit.id, formData);
    } else {
      createGearPal(formData);
    }
  };

  // Form con validazione Zod per LivePal
  const {
    register,
    handleSubmit: handleLivePalSubmit,
    formState: {
      errors: livePalErrors,
      isValid: isLivePalValid,
      touchedFields: livePalTouchedFields,
      isSubmitted: isLivePalSubmitted,
    },
    reset: resetLivePalForm,
    setValue: setLivePalValue,
    watch: watchLivePal,
    trigger: triggerLivePal,
  } = useForm<CreateLivePalForm>({
    resolver: zodResolver(CreateLivePalFormSchema),
    mode: "onTouched", // Valida dopo che l'utente tocca il campo
    defaultValues: {
      name: "",
      address: "",
      zipCode: "",
      city: "",
      region: "",
      country: "Italy",
      website: "",
      email: "",
      capacity: 0,
      venueType: "acoustic",
      accommodation: false,
      meals: false,
      coordinates: { lat: 0, lng: 0 },
      contacts: [],
      equipment: [],
    },
  });

  // Form con validazione Zod per GearPal
  const {
    register: registerGearPal,
    handleSubmit: handleGearPalSubmit,
    formState: {
      errors: gearPalErrors,
      isValid: isGearPalValid,
      touchedFields: gearPalTouchedFields,
      isSubmitted: isGearPalSubmitted,
    },
    reset: resetGearPalForm,
    setValue: setGearPalValue,
    watch: watchGearPal,
    trigger: triggerGearPal,
  } = useForm<CreateGearPalForm>({
    resolver: zodResolver(CreateGearPalFormSchema),
    mode: "onTouched", // Valida dopo che l'utente tocca il campo
    defaultValues: {
      name: "",
      zipcode: "",
      city: "",
      region: "",
      country: "Italy",
      coordinates: { lat: 0, lng: 0 },
      contacts: [],
      items: [] as GearPalItemForm[],
    },
  });

  // Forza la validazione quando cambiano items o contacts
  useEffect(() => {
    if (palType === "GearPal") {
      const values = watchGearPal();
      // Forza sempre la validazione per aggiornare lo stato del form
      triggerGearPal();
    }
  }, [palType, watchGearPal, triggerGearPal]);

  // Forza la validazione quando cambia LivePal
  useEffect(() => {
    if (palType === "LivePal") {
      triggerLivePal();
    }
  }, [palType, watchLivePal, triggerLivePal]);

  // Reset del form quando viene chiuso
  useEffect(() => {
    if (!isOpen) {
      setPalType(null);
      // Reset dei form RHF alla chiusura del modal
      try {
        resetLivePalForm();
      } catch {}
      try {
        resetGearPalForm();
      } catch {}
    }
  }, [isOpen]);

  // Imposta il tipo di Pal quando viene passato come prop
  useEffect(() => {
    if (propPalType) {
      setPalType(propPalType);
    }
  }, [propPalType]);

  // Popola il form quando apri in modalità edit per LivePal
  useEffect(() => {
    if (isEdit && palToEdit && palToEdit.type === "LivePal") {
      const livePal = palToEdit as LivePal;

      resetLivePalForm({
        name: livePal.name,
        address: livePal.address,
        zipCode: livePal.zipCode,
        city: livePal.city,
        region: livePal.region,
        country: livePal.country || "Italy",
        website: livePal.website || "",
        email: livePal.email || "",
        capacity: livePal.capacity,
        venueType: livePal.venueType,
        accommodation: livePal.accommodation,
        meals: livePal.meals,
        coordinates: livePal.coordinates,
        contacts: livePal.contacts || [],
        equipment: livePal.systems?.flatMap((s) => s.itemIds || []) || [],
      });
    }
  }, [isEdit, palToEdit, resetLivePalForm]);

  // Popola il form quando apri in modalità edit per GearPal
  useEffect(() => {
    if (isEdit && palToEdit && palToEdit.type === "GearPal") {
      const gearPal = palToEdit as GearPal;

      resetGearPalForm({
        name: gearPal.name,
        zipcode: gearPal.zipCode,
        city: gearPal.city,
        region: gearPal.region,
        country: gearPal.country || "Italy",
        coordinates: gearPal.coordinates,
        contacts: gearPal.contacts || [],
        items:
          gearPal.items?.map((item) => ({
            name: item.name,
            categoryId: item.categoryId.toString(), // Usa il categoryId numerico convertito in stringa
            externalLink: item.externalLink || "",
            categoryLabel: `${item.category} > ${item.subcategory} > ${item.label}`, // Path completo tradotto
          })) || [],
      });
    }
  }, [isEdit, palToEdit, resetGearPalForm]);

  if (!isOpen) return null;

  // Handler per chiudere il modal cliccando fuori dal pannello
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Chiudi solo se il click è sull'overlay stesso, non sui suoi figli
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[1002]"
      onClick={handleOverlayClick}
    >
      <div className="bg-[#2d1b4e] border border-[#6E11B04D] rounded-modal shadow-2xl max-w-2xl w-full max-h-[80vh] sm:max-h-[90vh] overflow-hidden text-white">
        <div className="overflow-y-auto custom-scrollbar max-h-[80vh] sm:max-h-[90vh] p-4 md:p-8">
          <div className="flex justify-between items-center mb-8 pb-4 border-b border-[#6E11B04D]">
            <div>
              <h2 className="text-2xl font-bold">
                <AddOrEditPalFormHeader palType={palType} isEdit={isEdit} />
              </h2>
              <p className="text-sm text-[#D1D5DC] mt-1">
                {isEdit
                  ? t(
                      "forms.editPalSubtitle",
                      "Modifica le informazioni del tuo Pal"
                    )
                  : t(
                      "forms.addPalSubtitle",
                      "Inserisci i dettagli per creare il tuo nuovo Pal"
                    )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-2 rounded-full hover:bg-white/5"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form
            onSubmit={
              palType === "LivePal"
                ? handleLivePalSubmit(handleLivePalFormSubmit)
                : handleGearPalSubmit(handleGearPalFormSubmit)
            }
            onClick={() => console.log("Form clicked!")}
          >
            {propPalType === "LivePal" && (
              <LivePalForm
                isEdit={isEdit}
                palToEdit={palToEdit as LivePal}
                registerLivePal={register}
                livePalErrors={livePalErrors}
                livePalTouchedFields={livePalTouchedFields}
                isLivePalSubmitted={isLivePalSubmitted}
                watchLivePal={watchLivePal}
                setLivePalValue={setLivePalValue}
                triggerLivePal={triggerLivePal}
                isLivePalValid={isLivePalValid}
                isAddingLivePal={isAddingLivePal}
                isUpdatingLivePal={isUpdatingLivePal}
              />
            )}
            {propPalType === "GearPal" && (
              <GearPalForm
                isEdit={isEdit}
                palToEdit={palToEdit as GearPal}
                registerGearPal={registerGearPal}
                gearPalErrors={gearPalErrors}
                gearPalTouchedFields={gearPalTouchedFields}
                isGearPalSubmitted={isGearPalSubmitted}
                watchGearPal={watchGearPal}
                setGearPalValue={setGearPalValue}
                triggerGearPal={triggerGearPal}
                isGearPalValid={isGearPalValid}
                isAddingGearPal={isAddingGearPal}
                isUpdatingGearPal={isUpdatingGearPal}
              />
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddOrEditPalForm;
